// DAS-2107: Helper for PortfolioAnalysisView data fetching
// Single module for all pa_view API calls.
// Uses the /api/pa_view dispatcher endpoint.

import api from '../functions/api';

/**
 * Fetch YF_Data for a batch of tickers.
 * Returns { yf_data: { AAPL: { currentPrice, PE, beta, sector, ... }, ... } }
 */
export const fetchYFData = async (tickers) => {
  if (!tickers || tickers.length === 0) return { yf_data: {} };
  const response = await api.post('/pa_view', { topic: 'yf_data', tickers });
  return response.data;
};

/**
 * Fetch Ticker_Performance_Data for a batch of tickers.
 * Returns { ticker_performance: { AAPL: { rsi_14, sharpe_ratio, ... }, ... } }
 */
export const fetchTickerPerformance = async (tickers) => {
  if (!tickers || tickers.length === 0) return { ticker_performance: {} };
  const response = await api.post('/pa_view', { topic: 'ticker_performance', tickers });
  return response.data;
};

/**
 * Fetch latest Trendsearch article per ticker.
 * Returns { news: { AXON: { article_title, article_url, impact_type, description, source_name, date, ... }, ... } }
 */
export const fetchNews = async (tickers) => {
  if (!tickers || tickers.length === 0) return { news: {} };
  const response = await api.post('/pa_view', { topic: 'news', tickers });
  return response.data;
};

/**
 * Evaluate Portfolio-scoped alert definitions against stock tickers.
 * Returns { alerts: [...], session_id: "...", summary: {...} }
 */
export const fetchAlerts = async (tickers, userId = null, source = 'portfolio') => {
  if (!tickers || tickers.length === 0) return { alerts: [], session_id: null, summary: { total: 0 } };
  const response = await api.post('/pa_view', { topic: 'alerts', tickers, user_id: userId, source });
  return response.data;
};

/**
 * Fetch dividend event data (past 12 months + projected next) for a batch of tickers.
 * Returns { dividend_data: { AAPL: { past: [...], next_estimated: {...} | null }, ... } }
 */
export const fetchDividendData = async (tickers) => {
  if (!tickers || tickers.length === 0) return { dividend_data: {} };
  const response = await api.post('/pa_view', { topic: 'dividend_data', tickers });
  return response.data;
};

/**
 * DAS-2168: Fetch deposit interest payout events for user's savings/deposits.
 * Returns { deposit_interest: [{ date, amount, rate, institution, ... }, ...] }
 */
export const fetchDepositInterest = async (userId) => {
  if (!userId) return { deposit_interest: [] };
  const response = await api.post('/pa_view', { topic: 'deposit_interest', tickers: [], user_id: userId });
  return response.data;
};

/**
 * Fetch pre-computed portfolio-level risk metrics (vol, VaR, Sharpe, drawdown).
 * Returns { portfolio_risk: { annualized_volatility, var_95_1d_eur, ... } | null }
 */
export const fetchPortfolioRisk = async (tickers) => {
  if (!tickers || tickers.length === 0) return { portfolio_risk: null };
  const response = await api.post('/pa_view', { topic: 'portfolio_risk', tickers });
  return response.data;
};

/**
 * Compute portfolio-level risk metrics on the fly (for file_drop route).
 * Sends positions to backend which downloads prices and computes vol/VaR/Sharpe/etc.
 * Takes ~5-10s due to yfinance download.
 */
export const fetchPortfolioRiskLive = async (tickers, positions, exposurePositions = null) => {
  if (!tickers || tickers.length === 0) return { portfolio_risk: null };
  const payload = { topic: 'portfolio_risk_live', tickers, positions };
  if (exposurePositions?.length > 0) payload.exposure_positions = exposurePositions;
  const response = await api.post('/pa_view', payload);
  return response.data;
};

/**
 * Fetch benchmark period returns (default: SPY).
 * Returns { benchmark: { ticker, name, returns: { "1D": x, "1W": y, ... } } }
 */
export const fetchBenchmark = async (benchmarkTicker = 'SPY') => {
  const response = await api.post('/pa_view', { topic: 'benchmark', tickers: [], benchmark_ticker: benchmarkTicker });
  return response.data;
};

/**
 * Generic dispatcher call for any pa_view topic.
 */
export const fetchAnalysis = async (topic, tickers, userId) => {
  const response = await api.post('/pa_view', { topic, tickers, user_id: userId });
  return response.data;
};
